import { useEffect, useReducer } from 'react'

import { Amount } from '@kunalabs-io/kai'

export const useInputAmountValidate = (inputValue: string, enforceDecimals: number | undefined) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    dispatch({ type: 'string_input_change', value: inputValue })
  }, [inputValue])

  useEffect(() => {
    dispatch({ type: 'enforce_decimals_change', value: enforceDecimals })
  }, [enforceDecimals])

  return {
    sanitizedInputValue: state.strAmount,
    amount: state.tokenAmount,
  }
}

const initialState: State = {
  tokenAmount: undefined,
  strAmount: '',
  enforceDecimals: undefined,
}

interface State {
  tokenAmount: Amount | undefined
  strAmount: string
  enforceDecimals: number | undefined
}

interface StringInputChange {
  type: 'string_input_change'
  value: string
}

interface EnforceDecimalsChange {
  type: 'enforce_decimals_change'
  value: number | undefined
}

type Action = StringInputChange | EnforceDecimalsChange

function truncateDecimals(num: string, decimals: number): string {
  const [int, dec] = num.split('.')
  if (decimals === 0) {
    return int
  }
  if (!dec || dec.length <= decimals) {
    return num
  }
  return `${int}.${dec.slice(0, decimals)}`
}

function countDecimals(value: string) {
  let decimals = 0
  const decPos = value.indexOf('.')
  if (decPos > 0) {
    decimals = value.length - decPos - 1
  }

  return decimals
}

function limitToDecimals(value: string, maxDecimals: number) {
  const decPos = value.indexOf('.')
  if (maxDecimals == 0 && decPos >= 0) {
    return value.slice(0, decPos)
  }

  const decimals = countDecimals(value)

  if (decimals && decimals > maxDecimals) {
    const shortenFor = decimals - maxDecimals
    return value.slice(0, value.length - shortenFor)
  }

  return value
}

function cleanNumberInput(value: string, maxDecimals?: number): string {
  // remove non-numbers and multiple decimal points
  let hasDecimalPoint = false
  let result = ''
  for (let i = 0; i < value.length; i++) {
    const char = value[i]
    if (char === '.') {
      if (hasDecimalPoint) {
        continue
      }
      result += '.'
      hasDecimalPoint = true
    }

    if (char >= '0' && char <= '9') {
      result += char
    }
  }

  // remove leading zeroes
  let firstNonZero = -1
  for (let i = 0; i < result.length; i++) {
    const char = result[i]
    firstNonZero++
    if (char !== '0') {
      break
    }
  }
  if (firstNonZero !== -1) {
    result = result.slice(firstNonZero)
  }

  // add 0 before "." if needed
  if (result.startsWith('.')) {
    result = '0' + result
  }

  // limit to decimals
  if (maxDecimals !== undefined) {
    result = limitToDecimals(result, maxDecimals)
  }

  return result
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'string_input_change': {
      const cleanAmount = cleanNumberInput(action.value, state.enforceDecimals)
      if (cleanAmount === '') {
        return {
          ...state,
          tokenAmount: undefined,
          strAmount: '',
        }
      }
      if (state.enforceDecimals === undefined) {
        return {
          ...state,
          strAmount: cleanAmount,
          tokenAmount: undefined,
        }
      }

      const decimals = state.enforceDecimals
      let nextAmount = Amount.fromNum(cleanAmount, decimals)
      if (state.tokenAmount && state.tokenAmount.equals(nextAmount)) {
        nextAmount = state.tokenAmount
      }

      return {
        ...state,
        strAmount: cleanAmount,
        tokenAmount: nextAmount,
      }
    }
    case 'enforce_decimals_change': {
      if (action.value === undefined) {
        return {
          ...state,
          enforceDecimals: undefined,
          tokenAmount: undefined,
        }
      }

      if (state.tokenAmount === undefined) {
        return {
          ...state,
          enforceDecimals: action.value,
        }
      }

      if (state.enforceDecimals === undefined || action.value < state.enforceDecimals) {
        let nextAmount = Amount.fromNum(
          truncateDecimals(state.tokenAmount.toString(), action.value),
          action.value
        )
        if (nextAmount.equals(state.tokenAmount)) {
          nextAmount = state.tokenAmount
        }

        return {
          ...state,
          tokenAmount: nextAmount,
          strAmount: limitToDecimals(state.strAmount, action.value),
          enforceDecimals: action.value,
        }
      }

      if (action.value > state.tokenAmount.decimals) {
        return {
          ...state,
          tokenAmount: Amount.fromNum(state.tokenAmount.toString(), action.value),
          enforceDecimals: action.value,
        }
      }

      return {
        ...state,
        enforceDecimals: action.value,
      }
    }
  }
}
